import { Button } from '@design-system';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ForwardedRef, forwardRef, useImperativeHandle } from 'react';

import { useProductCheckoutLink } from '../../../features/checkout/hooks/useProductCheckoutLink';
import {
  selectedSetWorkIds,
  withWorksQuery,
} from '../../../features/checkout/utils/worksQuery';
import { purchasingState } from '../../../helpers/purchasing';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { TrackBuynowClick } from '../../../utils/analytics';
import { ProductGatedRelease } from '../../Content/GatedRelease/ProductGatedRelease';
import { SkeletonCta } from './ctas/Skeleton';
import { TimedPurchaseCta } from './ctas/TimedPurchase';
import { FewLeft } from './FewLeft';
import { NFTPrintServiceForm } from './NFTPrintServiceForm';

type Props<T extends ProductPurchaseDetails> = {
  product: ProductType;
  purchasingController: ProductPurchasingController<T>;
};

const PurchaseForm = forwardRef(
  <T extends ProductPurchaseDetails>(
    { product, purchasingController }: Props<T>,
    ref: ForwardedRef<{
      onBuyNowClick: () => void;
      onViewAsSetClick: () => void;
    }>,
  ) => {
    const { t } = useTranslation('product');
    const router = useRouter();
    const checkoutLink = useProductCheckoutLink();

    const goToCheckout = async (workIds: string[]) => {
      await TrackBuynowClick({
        canBeFramed: !!purchasingController.product?.separateFrameAvailable,
        frameGlazingName: purchasingController.frame?.variant.title,
        frameUpgradeAvailable: Boolean(purchasingController.frameOptions),
        isFramed: !!purchasingController.frame,
        price: purchasingController.product?.price,
        product,
      });

      if (checkoutLink) {
        void router.push(withWorksQuery(checkoutLink, workIds));
        return;
      }

      // Works the checkout doesn't take (no purchase options, or not open to
      // the checkout yet) go straight to Shopify.
      purchasingController.goToCheckout();
    };

    // A `?collectionSet=` link picks set members on the page; the checkout
    // opens with them. Otherwise it opens on the main work.
    const onBuyNowClick = () =>
      goToCheckout(
        router.query.collectionSet
          ? selectedSetWorkIds(purchasingController.collectionSet)
          : [],
      );

    // "View as set" opens the checkout with every work in the set, leaving
    // the page's own selection and frame as they are.
    const onViewAsSetClick = () =>
      goToCheckout(
        selectedSetWorkIds(
          purchasingController.collectionSet?.map((work) => ({
            ...work,
            selected: true,
          })),
        ),
      );

    useImperativeHandle(ref, () => ({
      onBuyNowClick,
      onViewAsSetClick,
    }));

    if (purchasingController.product === null) {
      return <SkeletonCta />;
    }

    const state = purchasingState({
      releaseType: product.releaseType,
      closingDate: product.closingDate,
      available: purchasingController?.product?.available,
      tags: purchasingController?.product?.tags,
    });

    const renderCta = () => {
      if (product.isNFTPrintService) {
        return (
          <NFTPrintServiceForm
            product={product}
            purchasingController={purchasingController}
            release={purchasingController.release}
            unlockCode={purchasingController.unlockCode}
          />
        );
      }

      if (product.releaseType === 'timed-release') {
        return (
          <TimedPurchaseCta
            onBuyNowClick={onBuyNowClick}
            product={product}
            purchasingController={purchasingController}
          />
        );
      }

      return (
        <Button
          disabled={!purchasingController.canPurchase}
          fullWidth
          label={t('goToCheckout')}
          loading={purchasingController.checkoutLoading}
          onClick={onBuyNowClick}
          variant="primary"
        />
      );
    };

    if (state === 'AVAILABLE') {
      return (
        <ProductGatedRelease
          product={product}
          purchasingController={purchasingController}
        >
          <FewLeft
            className="mb-6"
            tags={purchasingController.product?.tags || []}
          />
          {renderCta()}
        </ProductGatedRelease>
      );
    }

    return null;
  },
);

export default PurchaseForm;
